import React, { useMemo } from 'react';
import { TDataInterface, TMaterialTableColumns } from './index.types';
import TableRow from './table-row';
import MaterialTableBody from '@material-ui/core/TableBody';
import { useGetSortAsc, useGetSortHeader } from './reducer/selectors';

const TableBody = <T extends TDataInterface>({
    data,
    columns,
    onClickRow,
}: {
    data?: T[];
    columns: TMaterialTableColumns<T>;
    onClickRow?: (value?: any) => void;
}) => {
    const sortHeader = useGetSortHeader() as keyof T;
    const sortAsc = useGetSortAsc();

    const dataFinal = useMemo(() => {
        if (!sortHeader) return data;
        if (!data || !Array.isArray(data)) return [];

        const defSortFunc = (a: T, b: T) => {
            if (a[sortHeader] > b[sortHeader]) return sortAsc ? -1 : 1;
            if (a[sortHeader] < b[sortHeader]) return sortAsc ? 1 : -1;
            if (a[sortHeader] === b[sortHeader]) return 0;
            return 0;
        };

        let colSortFunc;

        columns.forEach((col) => {
            if (col.title !== sortHeader && col.key !== sortHeader) return;
            if (col.sortFunc) colSortFunc = col.sortFunc(sortAsc);
            return;
        });

        return [...data.sort(colSortFunc || defSortFunc)];
    }, [columns, data, sortAsc, sortHeader]);

    const retData = useMemo(
        () =>
            (dataFinal &&
                Array.isArray(dataFinal) &&
                dataFinal.map((row) => {
                    const rowKey: string = JSON.stringify(
                        'id' in row && (row['id'] as string),
                    );

                    return (
                        <TableRow
                            key={rowKey}
                            rowKey={rowKey}
                            rowData={row}
                            columns={columns}
                            onClickRow={onClickRow}
                        />
                    );
                })) ||
            null,
        [columns, dataFinal, onClickRow],
    );

    return <MaterialTableBody>{retData}</MaterialTableBody>;
};

export default TableBody;
